// 时间线页：Phase 1 完整版。
// - limit 切换（25/50/100/200）走 URL search params 持久化。
// - cursor 分页是 ephemeral 状态（本地 state），按"加载更多"追加 items。
//   next_cursor==0 时按钮禁用并显示"没有更早数据"。
// - 视图层（TimelineView）跟路由解耦，vitest 直接 mount。
//
// 因为列表是"累积"的、跨多次请求拼起来的，QueryView 那种"单一 data 三态"
// 不太适用；这里手写 loading / error / empty 分支。

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { apiGet } from "@/api/client";
import { API_TIMELINE } from "@/api/endpoints";
import type { TimelineEntry, TimelineListResponse } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { Empty } from "@/components/ui/Empty";
import { ErrorPanel } from "@/components/ui/ErrorPanel";
import { formatMs } from "@/lib/format";

export const TIMELINE_LIMIT_OPTIONS = [25, 50, 100, 200] as const;
const DEFAULT_LIMIT = 50;

export interface TimelineSearch {
  // limit 可选，让 <Link to="/timeline" search={{}}> 也满足类型。
  limit?: number;
}

function parseTimelineSearch(search: Record<string, unknown>): TimelineSearch {
  const raw = Number(search.limit);
  const limit = (TIMELINE_LIMIT_OPTIONS as readonly number[]).includes(raw)
    ? raw
    : undefined;
  return { limit };
}

export const Route = createFileRoute("/timeline")({
  validateSearch: parseTimelineSearch,
  component: TimelineRouteComponent,
});

function TimelineRouteComponent() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/timeline" });
  return (
    <TimelineView
      search={search}
      onChange={(next) => navigate({ search: next, replace: false })}
    />
  );
}

interface TimelineViewProps {
  search: TimelineSearch;
  onChange: (next: TimelineSearch) => void;
}

export function TimelineView({ search, onChange }: TimelineViewProps) {
  // 当前要请求的 cursor。0 = 取最新一页。
  const [cursor, setCursor] = useState<number>(0);
  // 累积条目列表（按 ts_ms 倒序追加）。
  const [accumulated, setAccumulated] = useState<TimelineEntry[]>([]);
  // 上一次响应里的 next_cursor，决定"加载更多"按钮是否可用。
  const [nextCursor, setNextCursor] = useState<number>(0);

  const effectiveLimit = search.limit ?? DEFAULT_LIMIT;

  // limit 改变时重置游标和累积列表，等于重新拉首页。
  useEffect(() => {
    setCursor(0);
    setAccumulated([]);
    setNextCursor(0);
  }, [effectiveLimit]);

  const q = useQuery({
    queryKey: ["timeline", { limit: effectiveLimit, cursor }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("limit", String(effectiveLimit));
      if (cursor > 0) params.set("cursor", String(cursor));
      return apiGet<TimelineListResponse>(`${API_TIMELINE}?${params.toString()}`);
    },
  });

  // 拿到新数据：cursor==0 覆盖，否则追加。
  useEffect(() => {
    if (!q.data) return;
    setAccumulated((prev) => (cursor === 0 ? q.data!.items : [...prev, ...q.data!.items]));
    setNextCursor(q.data.next_cursor ?? 0);
  }, [q.data, cursor]);

  const hasMore = nextCursor > 0;
  const isFirstLoad = q.isLoading && accumulated.length === 0;
  const showError = q.isError && accumulated.length === 0;

  return (
    <div className="space-y-4">
      <label className="block text-xs">
        <span className="mb-1 block text-[hsl(var(--muted-foreground))]">limit</span>
        <select
          aria-label="limit"
          value={effectiveLimit}
          onChange={(e) => {
            const next = Number(e.target.value) || DEFAULT_LIMIT;
            onChange({ limit: next });
          }}
          className="h-8 rounded border border-[hsl(var(--input))] bg-transparent px-2 text-sm"
        >
          {TIMELINE_LIMIT_OPTIONS.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </label>

      {isFirstLoad ? (
        <div
          role="status"
          aria-label="加载中"
          className="space-y-2 animate-pulse"
        >
          <div className="h-4 w-1/3 bg-[hsl(var(--muted))] rounded" />
          <div className="h-4 w-2/3 bg-[hsl(var(--muted))] rounded" />
          <div className="h-4 w-1/2 bg-[hsl(var(--muted))] rounded" />
        </div>
      ) : showError ? (
        <ErrorPanel error={q.error} retry={() => q.refetch()} />
      ) : accumulated.length === 0 ? (
        <Empty title="暂无数据" />
      ) : (
        <ul className="divide-y divide-[hsl(var(--border))]">
          {accumulated.map((row, idx) => (
            <li key={`${row.ts_ms}-${idx}`} className="py-2">
              <div className="flex items-baseline gap-3 text-sm">
                <span className="font-mono text-xs text-[hsl(var(--muted-foreground))]">
                  {formatMs(row.ts_ms)}
                </span>
                <span className="rounded bg-[hsl(var(--muted))] px-1.5 py-0.5 text-xs">
                  {row.kind}
                </span>
                <span className="font-medium">{row.action}</span>
                <span className="text-[hsl(var(--muted-foreground))]">@ {row.actor}</span>
              </div>
              {row.payload_json ? (
                <div className="mt-1 font-mono text-xs text-[hsl(var(--muted-foreground))]">
                  {row.payload_json.length > 200
                    ? `${row.payload_json.slice(0, 200)}…`
                    : row.payload_json}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center gap-3">
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={!hasMore || q.isFetching}
          onClick={() => {
            if (!hasMore) return;
            setCursor(nextCursor);
          }}
        >
          {q.isFetching && hasMore
            ? "加载中…"
            : hasMore
              ? "加载更多"
              : "没有更早数据"}
        </Button>
        <span className="text-xs text-[hsl(var(--muted-foreground))]">
          已加载 {accumulated.length} 条
        </span>
      </div>
    </div>
  );
}
